import { ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate } from '@angular/router';
import { from, Observable, Observer, of } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import { take } from 'rxjs/internal/operators';

@Injectable()
export class SequentialGuard implements CanActivate {
  constructor(private injector: Injector) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // clone the guards, otherwise it will destroy the existing ones
    const guards = Object.assign([], route.data['sequentialGuards']);

    const observable = new Observable<boolean>(observer => {
      this.processGuards(route, state, guards, observer);
    });

    return observable;
  }

  private processGuards(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    guards: any[],
    observer: Observer<boolean>
  ) {
    if (!guards || guards.length === 0) {
      observer.next(true);
      observer.complete();
      return;
    }

    const guardClass = guards.shift();

    const guard: CanActivate = this.injector.get(guardClass);

    const canActivateReturn = guard.canActivate(route, state);

    let canActivateObservable: Observable<boolean>;

    if (canActivateReturn.constructor.name === 'Boolean') {
      canActivateObservable = of(<boolean>canActivateReturn);
    } else if (canActivateReturn.constructor.name === 'ZoneAwarePromise') {
      canActivateObservable = <Observable<boolean>>from(<Promise<boolean>>canActivateReturn);
    } else {
      canActivateObservable = <Observable<boolean>>canActivateReturn;
    }

    canActivateObservable.pipe(take(1)).subscribe(
      response => {
        if (response) {
          this.processGuards(route, state, guards, observer);
        } else {
          observer.next(false);
          observer.complete();
        }
      },
      error => {
        observer.next(false);
        observer.complete();
      }
    );
  }
}
